import { deepEqual } from '@flowus/common';
import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { LoadingContainer } from 'src/common/components/loading-container';
import { useIsScrolling } from 'src/common/hooks/use-scroll-mask';
import { ImagesProvider } from 'src/components/images-provider';
import { set_TocComponent } from 'src/components/move-to/toc-list';
import { useFavoritePages, useHasFavoritePages } from 'src/hooks/favorite/use-favorite-pages';
import {
  ID_ASIDE_FAVORITE,
  ID_ASIDE_PRIVATE,
  ID_ASIDE_SHARE,
  ID_ASIDE_SPACE,
  ID_TOC_CONTAINER,
} from 'src/hooks/page/use-dnd/helper';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { useCurrentSpace } from 'src/hooks/space';
import { fetchUserRoot, TYPE_SPACE_VIEWS, useCurrentUser } from 'src/hooks/user';
import { usePageRootLoaded, setAppUiState } from 'src/services/app/hook';
import { useGetSyncService } from 'src/services/sync';
import { ScrollRefContext } from '../../page-doc/context';
import { DragPreview } from './drag-preview';
import { NewPage } from './new-page';
import { TocGroupHeader } from './toc-group-header';
import { TocItem } from './toc-item';
import { TocType } from './types';
import { useTocIds } from './use-toc-id';
import { useGetPageId } from 'src/utils/getPageId';
import { useFetchPage } from 'src/hooks/page';
import { PrivateMoreButton } from './private-more-button';

// 私人页面限制显示的数量
const PRIVATE_PAGES_LIMIT = 10;

interface Props {
  className?: string;
}
/**
 * 页面目录树
 */
export const Toc: FC<Props> = (props) => {
  const currentSpace = useCurrentSpace();
  const currentUser = useCurrentUser();
  const isGuest = useIsGuest();
  const ref = useRef<HTMLDivElement>(null);
  const favoritePages = useFavoritePages();
  const hasFavorites = useHasFavoritePages();
  const sync = useGetSyncService();
  const pageRootLoaded = usePageRootLoaded();
  const isScrolling = useIsScrolling(ref);
  const tocIds = useTocIds(currentSpace.uuid, currentUser.uuid, isGuest);
  const pageId = useGetPageId();
  const fetchPage = useFetchPage();

  // 处理私人页面显示逻辑
  const privatePages = tocIds.person || [];
  const hasMorePrivatePages = privatePages.length > PRIVATE_PAGES_LIMIT;
  const visiblePrivatePages = hasMorePrivatePages
    ? privatePages.slice(0, PRIVATE_PAGES_LIMIT)
    : privatePages;

  // 处理打开"更多"面板
  const handleOpenMorePages = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // 使用 setAppUiState 设置状态来打开私人页面列表
    setAppUiState({ $showPrivateMore: true });
  };

  useEffect(() => {
    if (!currentUser.currentSpaceViewId) return;
    return sync.subscribeSpaceView(currentUser.currentSpaceViewId, (_) => {
      void fetchUserRoot(TYPE_SPACE_VIEWS);
    });
  }, [sync, currentUser.currentSpaceViewId]);
  useEffect(() => {
    if (!currentSpace.uuid) return;
    return sync.subscribeTabVisible(currentSpace.uuid, (_) => {
      void fetchUserRoot(TYPE_SPACE_VIEWS);
    });
  }, [sync, currentSpace.uuid]);
  useEffect(() => {
    if (!pageId) return;
    return sync.subscribeTabVisible(pageId, (_) => {
      void fetchPage(pageId);
    });
  }, [sync, pageId, fetchPage]);

  useEffect(() => {
    if (!isScrolling) {
      ref.current?.focus();
    }
  }, [isScrolling]);

  if (!pageRootLoaded) {
    return <LoadingContainer />;
  }

  return (
    <ImagesProvider uuid={currentSpace.uuid}>
      <ScrollRefContext.Provider value={ref}>
        <div
          id={ID_TOC_CONTAINER}
          ref={ref}
          className={cx(
            'relative overflow-y-auto overflow-x-hidden select-none pb-5',
            props.className
          )}
        >
          <div className={cx('space-y-5', isScrolling ? 'pointer-events-none' : '')}>
            {hasFavorites && (
              <TocGroupHeader id={ID_ASIDE_FAVORITE} type={TocType.FAVORITE} title="快速访问">
                {favoritePages.map((id) => (
                  <RenderTocItem key={id} id={id} />
                ))}
              </TocGroupHeader>
            )}

            {!isGuest && (
              <TocGroupHeader
                id={ID_ASIDE_SPACE}
                type={TocType.SPACE}
                tooltipDesc="空间内的所有成员均可访问"
                title="空间"
              >
                {tocIds.team.map((id) => (
                  <RenderTocItem key={id} id={id} />
                ))}
                {tocIds.team.length === 0 && <NewPage uuid={currentSpace.uuid} deep={0} />}
              </TocGroupHeader>
            )}

            {!!tocIds.share.length && (
              <TocGroupHeader
                id={ID_ASIDE_SHARE}
                type={TocType.SHARE}
                tooltipDesc="你分享的和别人分享给你的都在这里"
                title="共享"
              >
                {tocIds.share.map((id) => (
                  <RenderTocItem key={id} id={id} />
                ))}
              </TocGroupHeader>
            )}

            {!isGuest && (
              <TocGroupHeader
                id={ID_ASIDE_PRIVATE}
                type={TocType.PRIVATE}
                tooltipDesc="只有你可以访问"
                title="私人"
              >
                {visiblePrivatePages.map((id) => (
                  <RenderTocItem key={id} id={id} />
                ))}
                {privatePages.length === 0 && <NewPage uuid={currentSpace.uuid} deep={0} />}
                {hasMorePrivatePages && (
                  <PrivateMoreButton onClick={(e) => handleOpenMorePages(e)} />
                )}
              </TocGroupHeader>
            )}
            {props.children}
          </div>
        </div>

        <DragPreview />
      </ScrollRefContext.Provider>
    </ImagesProvider>
  );
};

const RenderTocItem = memo((props: { id: string }) => {
  const { id } = props;
  return <TocItem key={id} uuid={id} deep={0} />;
}, deepEqual);
set_TocComponent(Toc);
